import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { FileSignature, User2, Building2, UserCheck } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { useDirectorsByBranch, useUpdateCluster } from "../api/clusters";
import type { ClusterType, DocumentationCluster } from "../types";
import { useSignatories } from "@/features/employee_agreements/hooks/useEmployeeAgreements";

interface Contractor {
  id: string;
  name: string;
}

interface Props {
  cluster: DocumentationCluster;
  onClose: () => void;
  onSaved: () => void;
}

type SignerKind = "contractor" | "employee";

/** Director/CEO-only dialog to change a cluster's name, type and its sprawdzający. */
export function EditClusterDialog({ cluster, onClose, onSaved }: Props) {
  const user = useAuthStore((s) => s.user);
  const isPaper = cluster.signing_mode === "paper";

  const [name, setName] = useState(cluster.name);
  const [clusterType, setClusterType] = useState<ClusterType>(cluster.cluster_type);
  const [signerKind, setSignerKind] = useState<SignerKind>(
    cluster.employee_signer ? "employee" : "contractor",
  );
  const [contractorId, setContractorId] = useState(cluster.contractor ?? "");
  const [employeeId, setEmployeeId] = useState(cluster.employee_signer ?? "");
  const [employeeAgreementId, setEmployeeAgreementId] = useState(
    cluster.employee_agreement ?? "",
  );
  const [signatureTypeId, setSignatureTypeId] = useState(cluster.signature_type ?? "");
  const [reviewingDirectorId, setReviewingDirectorId] = useState(
    cluster.reviewing_director ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const { data: contractors } = useQuery({
    queryKey: ["contractors-design-reviewers"],
    queryFn: () =>
      apiClient
        // Pozycja „sprawdzenie dokumentacji projektowej" w umowie ramowej albo dawny typ
        // kartoteki — ta sama reguła, którą backend sprawdza przy zapisie klastra.
        .get<{ data: Contractor[] }>("/contractors/", {
          params: { eligible_for: "design_review" },
        })
        .then((r) =>
          (r.data.data as unknown as { results?: Contractor[] }).results ??
          (r.data.data as unknown as Contractor[])
        ),
  });
  // Dotychczasowy podpisujący zostaje do wyboru, nawet jeśli jego pozycję wycofano —
  // backend sprawdza tylko NOWY wybór.
  const designReviewers: Pick<Contractor, "id" | "name">[] = [...(contractors ?? [])];
  if (cluster.contractor && !designReviewers.some((c) => c.id === cluster.contractor)) {
    designReviewers.push({ id: cluster.contractor, name: cluster.contractor_name ?? "—" });
  }

  const { data: signatories = [] } = useSignatories();
  const selectedSignatory = signatories.find((s) => s.id === employeeId);

  // Keep the cluster's current UR/signature when its signer stays selected;
  // picking a different employee resets to their active agreement.
  useEffect(() => {
    if (employeeId === (cluster.employee_signer ?? "")) {
      setEmployeeAgreementId(cluster.employee_agreement ?? "");
      setSignatureTypeId(cluster.signature_type ?? "");
      return;
    }
    setEmployeeAgreementId(selectedSignatory?.active_agreement_id ?? "");
    setSignatureTypeId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, selectedSignatory?.active_agreement_id]);

  const activeSignatureTypes = (selectedSignatory?.signature_types ?? []).filter(
    (t) => !t.is_deleted,
  );
  const selectedSignatureType = activeSignatureTypes.find((t) => t.id === signatureTypeId);

  // Self-paid guard (mirrors the backend rule): fires only when the
  // signer/signature assignment actually changes.
  const assignmentChanged =
    employeeId !== (cluster.employee_signer ?? "")
    || signatureTypeId !== (cluster.signature_type ?? "");
  const isSelfPaidBlocked =
    clusterType === "designer_signature"
    && signerKind === "employee"
    && !!user
    && !user.is_ceo
    && employeeId === user.id
    && !!selectedSignatureType
    && !selectedSignatureType.is_free
    && assignmentChanged;

  const { data: directors = [] } = useDirectorsByBranch();
  const updateMutation = useUpdateCluster(cluster.id);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Podaj nazwę klastra.");
      return;
    }

    if (clusterType === "director_acceptance") {
      if (!reviewingDirectorId) {
        setError("Wybierz dyrektora sprawdzającego.");
        return;
      }
    } else {
      if (signerKind === "contractor" && !contractorId) {
        setError("Wybierz projektanta.");
        return;
      }
      if (signerKind === "employee" && (!employeeId || !employeeAgreementId || !signatureTypeId)) {
        setError("Wybierz pracownika i typ podpisu.");
        return;
      }
      if (isSelfPaidBlocked) {
        setError(
          "Nie możesz przypisać sobie płatnego typu podpisu — "
          + "może to zrobić wyłącznie CEO. Wybierz darmowy typ podpisu.",
        );
        return;
      }
    }

    try {
      if (clusterType === "director_acceptance") {
        await updateMutation.mutateAsync({
          name: trimmedName,
          cluster_type: "director_acceptance",
          reviewing_director: reviewingDirectorId,
        });
      } else {
        await updateMutation.mutateAsync({
          name: trimmedName,
          cluster_type: "designer_signature",
          contractor: signerKind === "contractor" ? contractorId : null,
          employee_signer: signerKind === "employee" ? employeeId : null,
          employee_agreement: signerKind === "employee" ? employeeAgreementId : null,
          signature_type: signerKind === "employee" ? signatureTypeId : null,
        });
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
      let msg: string | null = null;
      if (typeof data === "string") {
        msg = data;
      } else if (data && typeof data === "object") {
        const flat = Object.values(data).flat();
        const first = flat.find((v) => typeof v === "string");
        if (typeof first === "string") msg = first;
      }
      setError(msg ?? "Nie udało się zapisać zmian. Sprawdź wybór sprawdzającego.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Edytuj klaster
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 break-words">
          {cluster.project_name}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-cluster-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Nazwa klastra <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-cluster-name"
              type="text"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="np. Dokumentacja budowlana — etap I"
            />
          </div>

          {/* Cluster type toggle — paper clusters keep their type */}
          {!isPaper && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Typ klastra
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setClusterType("designer_signature")}
                  className={clsx(
                    "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                    clusterType === "designer_signature"
                      ? "border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/40"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300",
                  )}
                >
                  <span className="flex items-center gap-2 font-medium text-sm">
                    <FileSignature className="h-4 w-4" /> Podpis projektanta
                  </span>
                  <span className="text-xs text-gray-500">Standardowy obieg podpisów</span>
                </button>
                <button
                  type="button"
                  onClick={() => setClusterType("director_acceptance")}
                  className={clsx(
                    "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                    clusterType === "director_acceptance"
                      ? "border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-950/40"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300",
                  )}
                >
                  <span className="flex items-center gap-2 font-medium text-sm">
                    <UserCheck className="h-4 w-4" /> Akceptacja dyrektora
                  </span>
                  <span className="text-xs text-gray-500">Dyrektor akceptuje dokumenty</span>
                </button>
              </div>
            </div>
          )}

          {clusterType === "director_acceptance" ? (
            <div>
              <SearchableSelect
                label="Dyrektor sprawdzający *"
                placeholder="— wybierz —"
                value={reviewingDirectorId}
                onChange={(e) => setReviewingDirectorId(e.target.value)}
                options={directors.map((d) => ({ value: d.id, label: d.full_name }))}
              />
              {directors.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Brak dyrektorów w oddziale.
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Signer kind toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sprawdzający / podpisujący
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSignerKind("contractor")}
                    className={clsx(
                      "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                      signerKind === "contractor"
                        ? "border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/40"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300",
                    )}
                  >
                    <span className="flex items-center gap-2 font-medium text-sm">
                      <Building2 className="h-4 w-4" /> Podwykonawca
                    </span>
                    <span className="text-xs text-gray-500">Firma zewnętrzna</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignerKind("employee")}
                    className={clsx(
                      "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                      signerKind === "employee"
                        ? "border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/40"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300",
                    )}
                  >
                    <span className="flex items-center gap-2 font-medium text-sm">
                      <User2 className="h-4 w-4" /> Pracownik wewnętrzny
                    </span>
                    <span className="text-xs text-gray-500">Z UR pracownik-firma</span>
                  </button>
                </div>
              </div>

              {signerKind === "contractor" ? (
                <div>
                  <SearchableSelect
                    label="Projektant sprawdzający *"
                    placeholder="— wybierz —"
                    value={contractorId}
                    onChange={(e) => setContractorId(e.target.value)}
                    options={designReviewers.map((c) => ({ value: c.id, label: c.name }))}
                  />
                  {designReviewers.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Brak podwykonawców z pozycją „Sprawdzenie dokumentacji projektowej” w umowie ramowej.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <SearchableSelect
                    label="Pracownik-podpisujący *"
                    placeholder="— wybierz —"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    options={signatories.map((s) => ({
                      value: s.id,
                      label: s.full_name,
                    }))}
                  />
                  {signatories.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Brak pracowników z uprawnieniem podpisywania. CEO musi nadać
                      UR pracownik-firma.
                    </p>
                  )}
                  {employeeId && (
                    <>
                      <SearchableSelect
                        label="Typ podpisu *"
                        placeholder={
                          !employeeAgreementId
                            ? "Brak aktywnej UR dla wybranego pracownika"
                            : "— wybierz —"
                        }
                        value={signatureTypeId}
                        onChange={(e) => setSignatureTypeId(e.target.value)}
                        options={activeSignatureTypes.map((t) => ({
                          value: t.id,
                          label: user?.is_ceo
                            ? t.is_free
                              ? `${t.name} (darmowy)`
                              : `${t.name} — ${t.rate} PLN / ${t.unit}`
                            : t.name,
                        }))}
                      />
                      {isSelfPaidBlocked && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded px-2 py-1.5 mt-1.5">
                          Nie możesz przypisać sobie płatnego typu podpisu.
                          Wybierz darmowy typ podpisu albo poproś CEO o zmianę.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {clusterType !== cluster.cluster_type && (
            <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded px-2 py-1.5">
              Zmiana typu klastra przełącza obieg dla wszystkich dokumentów,
              które nie zostały jeszcze sprawdzone.
            </p>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
            <Button type="submit" disabled={updateMutation.isPending || isSelfPaidBlocked}>
              {updateMutation.isPending ? "Zapisywanie…" : "Zapisz zmiany"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
